import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Alert from "react-bootstrap/Alert";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Stack from "react-bootstrap/Stack";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPlus,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";

import { CreateSubmissionBindingModel, MachineViewModel, ValidationProblemDetails } from "../apiClient";
import { useApiClient } from "../helpers/useApiClient";
import { MainLayout } from "../layouts/mainLayout";
import { RegexConstants } from "../constants";
import { ApiError } from "../components/apiError";
import { RecaptchaDisclaimer } from "../components/recaptchaDisclaimer";

const SubmitRetailerSchema = Yup.object().shape({
  name: Yup.string().required("Please enter your name"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Please enter your email address so we can confirm the submission"),
  retailerName: Yup.string().required("Please enter the store or retailer name"),
  retailerPostcode: Yup.string()
    .required("Please enter the retailer postcode")
    .matches(RegexConstants.Postcode, "Please enter a valid UK postcode"),
  comment: Yup.string(),
  machines: Yup.array().min(1, "Please select at least one supported machine or format"),
});

export const SubmitRetailer = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const apiClient = useApiClient();
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<Error | ValidationProblemDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [machines, setMachines] = useState<MachineViewModel[] | null>(null);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      retailerName: "",
      retailerPostcode: "",
      comment: "",
      machines: [] as string[],
    },
    validationSchema: SubmitRetailerSchema,
    onSubmit: async (values) => {
      setError(null);
      setSuccess(false);
      try {
        await apiClient.submissionsPOST(
          new CreateSubmissionBindingModel({
            name: values.name,
            email: values.email,
            retailerName: values.retailerName,
            retailerPostcode: values.retailerPostcode.trim().toUpperCase(),
            comment: values.comment,
            machines: values.machines,
            captcha: executeRecaptcha ? await executeRecaptcha("submitRetailer") : undefined,
          })
        );
        setSuccess(true);
        formik.resetForm();
      } catch (err) {
        console.error(err);
        if (err instanceof ValidationProblemDetails || err instanceof Error) {
          setError(err);
        }
      }
    },
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const machinesData = await apiClient.machines();
        setMachines(machinesData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [apiClient]);

  return (
    <MainLayout title="Submit Retailer" loading={loading || formik.isSubmitting}>
      <Container className="py-4" style={{ maxWidth: 780 }}>
        <Link to="/" className="kf-back-link">
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to home</span>
        </Link>

        <ApiError error={error} />

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(false)} className="mb-4">
            <div className="d-flex align-items-center gap-2 mb-1">
              <FontAwesomeIcon icon={faCircleCheck} size="lg" />
              <Alert.Heading className="mb-0 fs-5">Thank you for submitting a retailer!</Alert.Heading>
            </div>
            <p className="mb-0 mt-2">
              Our community team will review the details and verify the stockist. Feel free to submit another if you know more!
            </p>
          </Alert>
        )}

        <div className="kf-form-card">
          <h1 className="h2 mb-2">Submit a Keg Retailer</h1>
          <p className="text-secondary mb-4">
            Spotted a local brewery, bottle shop, or retailer stocking draught kegs? Add them to the map to help fellow beer fans find stock.
          </p>

          <Form onSubmit={formik.handleSubmit}>
            <Stack gap={4}>
              {/* Contributor Details */}
              <div className="p-3 rounded-3" style={{ background: "rgba(255, 255, 255, 0.02)", border: "1px solid rgba(255, 255, 255, 0.06)" }}>
                <h3 className="h6 mb-3 fw-bold">1. Your Details</h3>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group controlId="name">
                      <Form.Label>Your Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. Sam Taylor"
                        name="name"
                        required
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={formik.isSubmitting}
                        isInvalid={!!(formik.touched.name && formik.errors.name)}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="email">
                      <Form.Label>Your Email <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="e.g. sam@example.com"
                        name="email"
                        required
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={formik.isSubmitting}
                        isInvalid={!!(formik.touched.email && formik.errors.email)}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Retailer Details */}
              <div className="p-3 rounded-3" style={{ background: "rgba(255, 255, 255, 0.02)", border: "1px solid rgba(255, 255, 255, 0.06)" }}>
                <h3 className="h6 mb-3 fw-bold">2. Retailer Information</h3>
                <Row className="g-3">
                  <Col md={7}>
                    <Form.Group controlId="retailerName">
                      <Form.Label>Store / Business Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. Craft Beer Oasis / Morrisons"
                        name="retailerName"
                        required
                        value={formik.values.retailerName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={formik.isSubmitting}
                        isInvalid={!!(formik.touched.retailerName && formik.errors.retailerName)}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.retailerName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group controlId="retailerPostcode">
                      <Form.Label>Store UK Postcode <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. SW1A 1AA"
                        name="retailerPostcode"
                        required
                        value={formik.values.retailerPostcode}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={formik.isSubmitting}
                        isInvalid={!!(formik.touched.retailerPostcode && formik.errors.retailerPostcode)}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.retailerPostcode}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Supported Machines */}
              <div className="p-3 rounded-3" style={{ background: "rgba(255, 255, 255, 0.02)", border: "1px solid rgba(255, 255, 255, 0.06)" }}>
                <h3 className="h6 mb-2 fw-bold">
                  3. Keg Formats Sold <span className="text-danger">*</span>
                </h3>
                <p className="text-secondary small mb-3">
                  Select all the draught systems and keg types this retailer stocks:
                </p>

                {machines && (
                  <Row className="g-2">
                    {machines.map((machine) => (
                      <Col sm={6} key={machine.id}>
                        <label
                          htmlFor={`machine-${machine.id}`}
                          className="d-flex align-items-center w-100 p-2 rounded-2 mb-0 user-select-none"
                          style={{
                            cursor: formik.isSubmitting ? "not-allowed" : "pointer",
                            background: formik.values.machines?.includes(machine.id!)
                              ? "rgba(135, 84, 78, 0.25)"
                              : "rgba(255, 255, 255, 0.03)",
                            border: formik.values.machines?.includes(machine.id!)
                              ? "1px solid rgba(135, 84, 78, 0.8)"
                              : "1px solid rgba(255, 255, 255, 0.08)",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <Form.Check.Input
                            type="checkbox"
                            name="machines"
                            id={`machine-${machine.id}`}
                            value={machine.id}
                            checked={formik.values.machines?.includes(machine.id!) ?? false}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={formik.isSubmitting}
                            className="me-2 mb-0"
                            style={{ cursor: "pointer" }}
                          />
                          <span className="flex-grow-1" style={{ cursor: "pointer" }}>
                            {machine.name}
                          </span>
                        </label>
                      </Col>
                    ))}
                  </Row>
                )}
                {formik.touched.machines && formik.errors.machines && (
                  <div className="text-danger small mt-2">
                    {formik.errors.machines as string}
                  </div>
                )}
              </div>

              {/* Comments / Notes */}
              <Form.Group controlId="comment">
                <Form.Label>Additional Information / Stock Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="e.g. Stocks Birra Moretti and Heineken Blade kegs, usually has 10+ in the chiller, open daily till 9pm..."
                  name="comment"
                  value={formik.values.comment}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting}
                  isInvalid={!!(formik.touched.comment && formik.errors.comment)}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.comment}
                </Form.Control.Feedback>
              </Form.Group>

              <RecaptchaDisclaimer />

              <div>
                <Button
                  variant="primary"
                  type="submit"
                  size="lg"
                  className="btn-submit d-inline-flex align-items-center gap-2"
                  disabled={formik.isSubmitting}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Submit Retailer for Review</span>
                </Button>
              </div>
            </Stack>
          </Form>
        </div>
      </Container>
    </MainLayout>
  );
};